import { PageContainer, ProTable } from '@ant-design/pro-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Flex, Modal, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import VideoCover from '@/common/components/VideoCover';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import AnalysisContentTrendChart from '../components/AnalysisContentTrendChart';
import { queryTraceContentTrend } from '../services/analysis';
import {
  queryTraceContentLogAccounts,
  queryTraceContentLogs,
  refreshTraceContentLogVideo,
} from '../services/content-logs';
import { formatTraceAccountName, formatTracePlatformName } from '../utils/platform';

const platformValueEnum = {
  douyin: { text: '抖音' },
  weibo: { text: '微博' },
  xhs: { text: '小红书' },
};

const getDateText = (value) => {
  if (!value) {
    return undefined;
  }
  return value.format ? value.format('YYYY-MM-DD') : value;
};

const getRangeQuery = (value) => ({
  end: getDateText(value?.[1]),
  start: getDateText(value?.[0]),
});

const ContentLogs = ({
  accountType = 'own',
  content = '查看作品7日内的监测数据，作为作品效果的基础数据查询工具。',
  showAccountColumn = false,
  title = '自有作品',
}) => {
  const { organizationId } = useOrganizationPath();
  const { message } = App.useApp();
  const tableScrollX = showAccountColumn ? 1224 : 1048;
  const actionRef = useRef(null);
  const [trendContent, setTrendContent] = useState(null);
  const [videoContent, setVideoContent] = useState(null);
  const [videoReloadKey, setVideoReloadKey] = useState(0);
  const { data: contentTrend = {}, isFetching: contentTrendLoading } = useQuery({
    enabled: Boolean(
      organizationId &&
      trendContent?.platform &&
      trendContent?.accountId &&
      trendContent?.contentId,
    ),
    queryFn: () =>
      queryTraceContentTrend({
        accountId: trendContent.accountId,
        accountType,
        contentId: trendContent.contentId,
        organizationId,
        platform: trendContent.platform,
      }),
    queryKey: [
      'trace-content-log-content-trend',
      accountType,
      organizationId,
      trendContent?.platform,
      trendContent?.accountId,
      trendContent?.contentId,
    ],
  });

  const accountColumn = {
    title: '账号',
    dataIndex: 'accountKey',
    ellipsis: true,
    fieldProps: {
      allowClear: true,
      optionFilterProp: 'label',
      placeholder: '全部账号',
      showSearch: true,
    },
    request: async () => {
      const accounts = await queryTraceContentLogAccounts({ accountType, organizationId });
      return accounts.map((account) => ({
        label: formatTraceAccountName(account),
        value: `${account.platform}:${account.accountId}`,
      }));
    },
    renderText: (_, record) => formatTraceAccountName(record) || '-',
    valueType: 'select',
    width: 176,
  };

  const { mutateAsync: refreshVideo, isPending: refreshingVideo } = useMutation({
    mutationFn: refreshTraceContentLogVideo,
    onSuccess: (payload) => {
      const updatedRecord = payload?.data || payload;
      if (updatedRecord?.videoUrl) {
        setVideoContent((current) => ({
          ...current,
          videoUrl: updatedRecord.videoUrl,
        }));
        setVideoReloadKey((key) => key + 1);
      }
      actionRef.current?.reload?.();
      message.success('视频已刷新');
    },
    onError: () => message.error('视频刷新失败，请重试'),
  });

  const handleRefreshVideo = () => {
    if (!organizationId || !videoContent?.id) {
      return;
    }
    refreshVideo({
      id: videoContent.id,
      organizationId,
    });
  };

  const handleOpenVideo = (record) => {
    setVideoContent(record);
    setVideoReloadKey((key) => key + 1);
  };

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      ellipsis: true,
      fixed: 'left',
      width: 320,
    },
    {
      title: '平台',
      dataIndex: 'platform',
      valueEnum: platformValueEnum,
      renderText: formatTracePlatformName,
      width: 88,
    },
    ...(showAccountColumn ? [accountColumn] : []),
    {
      title: '获赞',
      dataIndex: 'likeCount',
      search: false,
      sorter: true,
      valueType: 'digit',
      width: 88,
    },
    {
      title: '评论',
      dataIndex: 'commentCount',
      search: false,
      sorter: true,
      valueType: 'digit',
      width: 88,
    },
    {
      title: '转发',
      dataIndex: 'shareCount',
      search: false,
      sorter: true,
      valueType: 'digit',
      width: 88,
    },
    {
      title: '收藏',
      dataIndex: 'collectCount',
      search: false,
      sorter: true,
      valueType: 'digit',
      width: 88,
    },
    {
      title: '记录时间',
      dataIndex: 'createdAt',
      search: false,
      sorter: true,
      valueType: 'dateTime',
      width: 176,
    },
    {
      title: '时间',
      dataIndex: 'createdAtRange',
      hideInTable: true,
      search: {
        transform: getRangeQuery,
      },
      valueType: 'dateRange',
    },
    {
      title: '操作',
      dataIndex: 'actions',
      fixed: 'right',
      search: false,
      width: 112,
      render: (_, record) => (
        <Flex gap="small">
          <Button type="link" onClick={() => setTrendContent(record)}>
            趋势
          </Button>
          <Button disabled={!record.videoUrl} type="link" onClick={() => handleOpenVideo(record)}>
            视频
          </Button>
        </Flex>
      ),
    },
  ];

  return (
    <PageContainer content={content}>
      <ProTable
        actionRef={actionRef}
        columns={columns}
        headerTitle={title}
        request={(params, sort) =>
          queryTraceContentLogs({
            ...params,
            accountType,
            latestOnly: true,
            organizationId,
            sort,
          })
        }
        rowKey="id"
        scroll={{ x: tableScrollX }}
        search={{ defaultCollapsed: false, labelWidth: 96 }}
        tableLayout="fixed"
      />
      <Modal
        destroyOnHidden
        footer={null}
        loading={contentTrendLoading}
        onCancel={() => setTrendContent(null)}
        open={Boolean(trendContent)}
        styles={{ body: { height: 478 } }}
        title={
          <Typography.Text ellipsis={{ tooltip: trendContent?.title }}>
            {trendContent?.title}
          </Typography.Text>
        }
        width={1088}
      >
        <AnalysisContentTrendChart dataSource={contentTrend.series || []} />
      </Modal>
      <Modal
        destroyOnHidden
        footer={null}
        onCancel={() => setVideoContent(null)}
        open={Boolean(videoContent)}
        title={
          <Typography.Text ellipsis={{ tooltip: videoContent?.title }}>
            {videoContent?.title}
          </Typography.Text>
        }
        width={1088}
      >
        <VideoCover
          autoPlay
          reloadKey={videoReloadKey}
          retrying={refreshingVideo}
          src={videoContent?.videoUrl}
          onRetry={handleRefreshVideo}
        />
      </Modal>
    </PageContainer>
  );
};

ContentLogs.propTypes = {
  accountType: PropTypes.oneOf(['own', 'rival']),
  content: PropTypes.string,
  showAccountColumn: PropTypes.bool,
  title: PropTypes.string,
};

export default ContentLogs;
